// Copyright (c) 2025, California Institute of Technology and contributors
//
// You should have received a copy of the licensing terms for this
// software included in the file "LICENSE" located in the top-level
// directory of this package. If you did not, you can view a copy at
// https://git.ligo.org/ngdd/arrakis-js/-/raw/main/LICENSE

import type { SeriesBlock } from "./block.js";
import type { Channel } from "./channel.js";
import Client from "./client.js";

/**
 * Count channels matching a set of conditions
 *
 * @param {string} [pattern] - Channel pattern to match channels with, using regular expressions
 * @param {string|Array<string>} [dataType] - If set, find all channels with these data types
 * @param {number} [minRate] - Minimum sample rate for channels
 * @param {number} [maxRate] - Maximum sample rate for channels
 * @param {string|Array<string>} [publisher] - If set, find all channels associated with these publishers
 * @returns {Promise<number>} A promise that resolves to the number of channels matching query
 */
export async function count(
  pattern?: string,
  dataType?: string | Array<string>,
  minRate?: number,
  maxRate?: number,
  publisher?: string | Array<string>,
): Promise<number> {
  const client = new Client();
  return client.count(pattern, dataType, minRate, maxRate, publisher);
}

/**
 * Find channels matching a set of conditions and stream results via Server-Sent Events (SSE)
 *
 * @param {string} [pattern] - Channel pattern to match channels with, using regular expressions
 * @param {string|Array<string>} [dataType] - If set, find all channels with these data types
 * @param {number} [minRate] - Minimum sample rate for channels
 * @param {number} [maxRate] - Maximum sample rate for channels
 * @param {string|Array<string>} [publisher] - If set, find all channels associated with these publishers
 * @returns {AsyncGenerator<Channel>} An async generator that yields channel objects as they are received via SSE
 */
export async function* find(
  pattern?: string,
  dataType?: string | Array<string>,
  minRate?: number,
  maxRate?: number,
  publisher?: string | Array<string>,
): AsyncGenerator<Channel> {
  const client = new Client();
  yield* client.find(pattern, dataType, minRate, maxRate, publisher);
}

/**
 * Stream timeseries data for a list of channels within a time range
 *
 * @param {string[]} channels - A list of channels to request
 * @param {number} [start] - GPS start time, in seconds; streams from now if omitted
 * @param {number} [end] - GPS end time, in seconds; streams indefinitely if omitted
 * @returns {AsyncGenerator<SeriesBlock>} An async generator yielding `SeriesBlock`s as they stream in
 */
export async function* stream(
  channels: string[],
  start?: number,
  end?: number,
): AsyncGenerator<SeriesBlock> {
  const client = new Client();
  yield* client.stream(channels, start, end);
}

/**
 * Fetch timeseries data for a list of channels within a time range
 *
 * @param {string[]} channels - A list of channels to request
 * @param {number} start - GPS start time, in seconds
 * @param {number} end - GPS end time, in seconds
 * @returns {Promise<SeriesBlock>} A promise that resolves to the block of timeseries data
 */
export async function fetch(
  channels: string[],
  start: number,
  end: number,
): Promise<SeriesBlock> {
  const client = new Client();
  return client.fetch(channels, start, end);
}

/**
 * Get channel metadata for channels requested
 *
 * @param {string[]} channels - List of channels to request
 * @returns {Promise<Record<string, Channel>>} A promise that resolves to a mapping of channel names to channel metadata
 */
export async function describe(
  channels: string[],
): Promise<Record<string, Channel>> {
  const client = new Client();
  return client.describe(channels);
}
